import { useState, useRef, useContext, useId } from "react";
import { useFactoryRef, useOnUnmount, useOnValueChange } from "@pjs/react-utilities";
import { floatingPanelMenuFactory } from "../aria-roles/menus/floating-menu/factories/FloatingPanelMenuFactory";
import { BoundaryContext } from "../boundary/Boundary.context";
import { MenuAriaModel } from "../aria-roles/menus/menu-event-adapter/types/MenuAriaModel";
import { AriaMenuStatus } from "../aria-roles/menus/menu-event-adapter/enums/AriaMenuStatus";
import { IFloatingPanelMenuItem } from "./interfaces/IFloatingPanelMenuItem";
import { FloatingPanelMenuContent } from "./FloatingPanelMenuContent.component";
import { FloatingPanelMenuCompound } from "./types/FloatingPanelMenuCompound";
import { FloatingPanelMenuPanel } from "./FloatingPanelMenuPanel.component";
import { FloatingPanelMenuTrigger } from "./FloatingPanelMenuTrigger.component";
import { FloatingPanelMenuContext } from "./FloatingPanelMenu.context";
import "./styles/floating-panel-menu.css";

const matchItemOnKeys = (item: IFloatingPanelMenuItem, currentKeys: string): boolean => item.text.toLowerCase().startsWith(currentKeys);

const initialModel: MenuAriaModel = {
    activeItemIndex: 0,
    isOpen: false,
    type: AriaMenuStatus.Closed
} as const;

export const FloatingPanelMenu: FloatingPanelMenuCompound = (props) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const [model, setModel] = useState<MenuAriaModel>(initialModel);
    const boundaryContext = useContext(BoundaryContext);
    const contentId = useId();

    const floatingMenu = useFactoryRef(floatingPanelMenuFactory, {
        boundaryRef: boundaryContext.element,
        floatingRef: contentRef,
        menuRef: menuRef,
        initialModel: model,
        matchItemOnKeys: matchItemOnKeys,
        onModelChange: setModel,
        placement: props.placement,
        triggerRef: triggerRef
    });

    useOnValueChange(() => floatingMenu.updateModel(model), [model]);
    useOnUnmount(() => floatingMenu.destroy());

    return (
        <FloatingPanelMenuContext.Provider
            value={{
                model: model,
                events: floatingMenu.events,
                triggerRef: triggerRef,
                contentRef: contentRef,
                contentId: contentId,
                menuRef: menuRef,
                items: props.items
            }}>
            <div className="cui-floating-panel-menu__container">{props.children}</div>
        </FloatingPanelMenuContext.Provider>
    );
};

FloatingPanelMenu.Trigger = FloatingPanelMenuTrigger;
FloatingPanelMenu.Menu = FloatingPanelMenuContent;
FloatingPanelMenu.Panel = FloatingPanelMenuPanel;
